import { useId, type CSSProperties } from "react";
import { bewegungReduziert } from "./bewegung";
import { leben as lebenFuer, type Leben } from "./darf";
import { useWelt } from "./einstellungen";
import tokens from "./tokens-w82.json";

/** Größen der Figur in px: lebhaft 124, ruhig 96, Randgeist 54, Platz der Akten und Randgeist 44, Sprechzeile und Chat 40
 *  (Vorlage `.figur-klein .geist`), klein 28. */
export type GeistGroesse = 124 | 96 | 54 | 44 | 40 | 28;
export type GeistZustand = "ruhe" | "flug" | "landen" | "rand";

/** Form des Briefgeists (Logo D-060, marke/briefgeist-glyph.svg; Figur wie R11): Leib, Klappe und Augen. */
const LEIB =
  "M11 31A20 20 0 0 1 51 31V50a3.333 3.333 0 0 1-6.667 0a3.333 3.333 0 0 1-6.667 0a3.333 3.333 0 0 1-6.667 0a3.333 3.333 0 0 1-6.667 0a3.333 3.333 0 0 1-6.667 0a3.333 3.333 0 0 1-6.667 0Z";
const KLAPPE = "M14 33.2L31 44.4L48 33.2L48 38L31 49.2L14 38Z";
const FUNKE = "M54 4Q54 12 62 12Q54 12 54 20Q54 12 46 12Q54 12 54 4Z";

/** Fünf Lichtteilchen (R11): Lage im Feld von 132 px, Dauer 6,4 bis 9 s (tokens.json dauern.leben.teilchen). */
const TEILCHEN = [
  { x: 12, y: 83, d: 7000, v: -1000, g: 3 },
  { x: 116, y: 52, d: 8500, v: -4000, g: 2 },
  { x: 31, y: 118, d: 6400, v: -2500, g: 2 },
  { x: 99, y: 123, d: 9000, v: -6000, g: 3 },
  { x: 130, y: 104, d: 7600, v: -3000, g: 2 },
];

/** Der Briefgeist als Figur (Inline-SVG `svg.geist`, für Vorlesen verborgen). Leben nach `leben()` (darf.ts): Atmen der
 *  Aura, Blinzeln, Blick, Funkeln, Schweben mit Boden, Lichtteilchen; reduzierte Bewegung und `?still` halten ihn still. */
export function Geist(props: { groesse?: GeistGroesse; zustand?: GeistZustand; leben?: Leben; className?: string; kennung?: string }) {
  const [welt] = useWelt();
  const groesse = props.groesse ?? (welt.fassung === "ruhig" ? 96 : 124);
  const l = props.leben ?? lebenFuer(welt.fassung, welt.praesenz, bewegungReduziert());
  const schweben = welt.fassung === "ruhig" ? tokens.dauern.leben.schweben.ruhig[welt.praesenz] : tokens.dauern.leben.schweben.lebhaft;
  const id = `fs${useId().replace(/[^a-zA-Z0-9_-]/g, "")}`;
  const stil = {
    "--geist-g": `${groesse}px`,
    "--geist-faktor": groesse / tokens.masse.geist[welt.fassung],
    "--geist-hub": `${l.schweben}px`,
    "--geist-neigung": `${l.neigung}deg`,
    "--geist-schweben-dauer": `${schweben.dauer}ms`,
  } as CSSProperties;
  const an = (wert: boolean) => (wert ? "" : undefined);
  return (
    <span
      className={`geist-figur ${props.className ?? ""}`}
      aria-hidden="true"
      data-testid={props.kennung}
      data-zustand={props.zustand ?? "ruhe"}
      data-groesse={groesse}
      data-atmen={an(l.atmen)}
      data-blinzeln={an(l.blinzeln)}
      data-blick={an(l.blick)}
      data-funkeln={an(l.funkeln)}
      data-schweben={an(l.schweben > 0)}
      data-teilchen={an(l.teilchen)}
      style={stil}
    >
      <span className="geist-aura" />
      {l.teilchen && (
        <span className="geist-teilchen">
          {TEILCHEN.map((t) => (
            <span
              key={t.x}
              style={{ left: `${(t.x / 132) * 100}%`, top: `${(t.y / 132) * 100}%`, "--t-g": `${t.g}px`, "--t-d": `${t.d}ms`, "--t-v": `${t.v}ms` } as CSSProperties}
            />
          ))}
        </span>
      )}
      <span className="geist-boden" />
      <span className="geist-schwebe">
        <span className="geist-atem">
          <span className="geist-sprung">
            <svg className="geist" viewBox="0 0 64 64" aria-hidden="true" focusable="false">
              <defs>
                <radialGradient id={id}>
                  <stop offset="0" style={{ stopColor: "var(--pgw-funke)", stopOpacity: 0.8 }} />
                  <stop offset="0.45" style={{ stopColor: "var(--pgw-funke)", stopOpacity: 0.3 }} />
                  <stop offset="1" style={{ stopColor: "var(--pgw-funke)", stopOpacity: 0 }} />
                </radialGradient>
              </defs>
              <circle className="funke-schein" cx="54" cy="12" r="11" fill={`url(#${id})`} />
              <path className="leib" d={LEIB} />
              <path className="mund" d={KLAPPE} />
              <g className="blick">
                <ellipse className="auge" cx="24" cy="25.5" rx="3.6" ry="3.6" />
                <ellipse className="auge" cx="38" cy="25.5" rx="3.6" ry="3.6" />
              </g>
              <path className="funke" d={FUNKE} />
            </svg>
          </span>
        </span>
      </span>
    </span>
  );
}
